import React, { useCallback, useEffect, useRef, useState } from "react";
import Modal from "../common/Modal";
import AiAssistantApi from "../../services/resources/AiAssistantApi";
import AuthService from "../../services/AuthService";
import {
  selectPreferredModel,
  extractSqlResultRows,
  createTurn,
} from "./aiAssistantHelpers";
import AiAssistantHistory from "./AiAssistantHistory";
import TurnCard from "./AiAssistantTurnCard";
import ChatIcon from "@material-ui/icons/ChatOutlined";

const DATA_SCOPE = {
  ENGAGEMENT: "engagement",
  ALL: "all",
};

const DEFAULT_DATA_SCOPE = DATA_SCOPE.ENGAGEMENT;

export default function AiAssistantModal({ onClose }) {
  const [query, setQuery] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [turns, setTurns] = useState([]);
  const [error, setError] = useState(null);
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState("");
  const [modelWarning, setModelWarning] = useState(null);
  const [dataScope, setDataScope] = useState(DEFAULT_DATA_SCOPE);
  const [selectedHistoryId, setSelectedHistoryId] = useState(null);
  const [conversationMessages, setConversationMessages] = useState(null);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const thinkingBoxRef = useRef(null);
  const textareaRef = useRef(null);
  const threadRef = useRef(null);

  const isMac = /Mac|iPhone|iPod|iPad/.test(navigator.userAgent);
  const shortcutLabel = isMac ? "⌘+Enter" : "Ctrl+Enter";

  const pendingToolsRef = useRef({});
  const allToolCallsRef = useRef([]);
  const abortControllerRef = useRef(null);
  const wasCancelledRef = useRef(false);
  const partialAnswerRef = useRef("");

  const hasTurns = turns.length > 0;

  const handleNew = useCallback(() => {
    setSelectedHistoryId(null);
    setQuery("");
    setTurns([]);
    setError(null);
    setConversationMessages(null);
    setActiveThreadId(null);
    setDataScope(DEFAULT_DATA_SCOPE);
    const { modelId, warning } = selectPreferredModel(models);
    setSelectedModel(modelId);
    setModelWarning(warning);
  }, [models]);

  const handleHistorySelect = useCallback(async (item) => {
    setSelectedHistoryId(item.threadID);
    setActiveThreadId(null);
    setConversationMessages(null);
    setQuery("");
    setModelWarning(null);

    const details = await AiAssistantApi.getConversationThread(item.threadID);

    setSelectedModel(details.model || "");
    setDataScope(details.dataScope || null);

    const historicalTurns = (details.turns || []).map((turn) => {
      let sqlQueries = [];
      if (turn.sqlQueries) {
        try {
          const parsed =
            typeof turn.sqlQueries === "string"
              ? JSON.parse(turn.sqlQueries)
              : turn.sqlQueries;
          if (Array.isArray(parsed)) {
            sqlQueries = parsed
              .filter((sql) => typeof sql === "string" && sql.trim())
              .map((sql) => ({
                sql,
                results: [],
              }));
          }
        } catch {
          // ignore parse errors
        }
      }

      const baseAnswer = turn.response || "";
      const answer =
        turn.status === "cancelled"
          ? `${baseAnswer}${baseAnswer ? "\n\n" : ""}_Cancelled by user._`
          : baseAnswer;

      return {
        ...createTurn(turn.question || ""),
        answer,
        thinkingExpanded: false,
        sqlQueries,
        metrics: {
          askedBy: turn.askedBy || null,
          numToolRounds: turn.numToolRounds ?? 0,
          durationSeconds: turn.durationSeconds ?? 0,
          inputTokens: turn.inputTokens ?? 0,
          outputTokens: turn.outputTokens ?? 0,
        },
        conversationId: turn.id ?? null,
        feedback: null,
      };
    });

    setTurns(historicalTurns);

    const hasError = (details.turns || []).some((t) => t.status === "error");
    if (hasError) {
      setError(
        "One or more turns in this conversation failed when originally run.",
      );
    } else {
      setError(null);
    }
  }, []);

  const updateCurrentTurn = useCallback((updater) => {
    setTurns((prev) => {
      if (prev.length === 0) {
        return prev;
      }
      const next = [...prev];
      const last = { ...next[next.length - 1] };
      updater(last);
      next[next.length - 1] = last;
      return next;
    });
  }, []);

  useEffect(() => {
    const fetchModels = async () => {
      try {
        const data = await AiAssistantApi.getAiModels();
        if (data.models && data.models.length > 0) {
          setModels(data.models);
          const { modelId, warning } = selectPreferredModel(data.models);
          setSelectedModel(modelId);
          setModelWarning(warning);
        }
      } catch (err) {
        setError("Failed to fetch models: " + err.message);
      }
    };

    fetchModels();
  }, []);

  useEffect(() => {
    if (!isLoading) {
      return;
    }

    if (thinkingBoxRef.current) {
      thinkingBoxRef.current.scrollTop = thinkingBoxRef.current.scrollHeight;
    }

    if (threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight;
    }
  }, [turns, isLoading]);

  const handleStreamEvent = useCallback(
    (data) => {
      switch (data.type) {
        case "thread":
          if (data.threadId != null) {
            setActiveThreadId(data.threadId);
          }
          break;
        case "conversation_saved":
          if (data.conversationId != null) {
            updateCurrentTurn((t) => {
              t.conversationId = data.conversationId;
            });
          }
          break;
        case "thinking":
          updateCurrentTurn((t) => {
            t.thinkingText += data.content;
          });
          break;
        case "thinking_start":
          break;
        case "text":
          updateCurrentTurn((t) => {
            if (!t.answer) {
              t.thinkingExpanded = false;
            }
            t.answer += data.content;
          });
          partialAnswerRef.current += data.content;
          break;
        case "answer":
          updateCurrentTurn((t) => {
            t.answer = data.content;
          });
          partialAnswerRef.current = data.content;
          break;
        case "usage":
          updateCurrentTurn((t) => {
            t.metrics = {
              askedBy: t.metrics?.askedBy ?? null,
              numToolRounds: t.metrics?.numToolRounds ?? 0,
              durationSeconds: t.metrics?.durationSeconds ?? 0,
              inputTokens: data.input_tokens ?? 0,
              outputTokens: data.output_tokens ?? 0,
            };
          });
          break;
        case "tool_use": {
          updateCurrentTurn((t) => {
            t.thinkingText += `\n[Using tool: ${data.name}]\n`;
          });
          const toolId = data.id;
          const sql =
            data.name === "execute_sql" && data.input?.sql
              ? data.input.sql
              : null;
          pendingToolsRef.current[toolId] = { name: data.name, sql };
          allToolCallsRef.current.push({
            type: "tool_use",
            id: toolId,
            name: data.name,
            sql,
          });
          break;
        }
        case "tool_result": {
          updateCurrentTurn((t) => {
            t.thinkingText += "\n[Tool result received]\n";
          });
          const resultToolId = data.id;
          const pendingTool = pendingToolsRef.current[resultToolId];
          allToolCallsRef.current.push({
            type: "tool_result",
            id: resultToolId,
            name: data.name,
            hasResult: Boolean(data.result),
          });
          if (pendingTool) {
            delete pendingToolsRef.current[resultToolId];
          }
          break;
        }
        case "complete": {
          updateCurrentTurn((t) => {
            t.thinkingExpanded = false;
          });
          if (data.sql_results && data.sql_results.length > 0) {
            updateCurrentTurn((t) => {
              data.sql_results.forEach((sqlResult) => {
                if (!sqlResult.sql) return;
                const exists = t.sqlQueries.some(
                  (q) => q.sql === sqlResult.sql,
                );
                if (!exists) {
                  const rows =
                    sqlResult.result && !sqlResult.error
                      ? extractSqlResultRows(sqlResult.result) || []
                      : [];
                  t.sqlQueries = [
                    ...t.sqlQueries,
                    {
                      sql: sqlResult.sql,
                      results: rows,
                      toolName: sqlResult.tool || "execute_sql",
                      error: sqlResult.error || null,
                    },
                  ];
                }
              });
            });
          }
          if (data.history) {
            setConversationMessages(data.history);
          }
          allToolCallsRef.current = [];
          pendingToolsRef.current = {};
          break;
        }
        case "error": {
          const source = data.source || "unknown";
          if (source === "backend-api") {
            setError(`🤖 AI API Error: ${data.content}`);
          } else if (source === "backend-mcp") {
            setError(`🔌 Database Connection Error: ${data.content}`);
          } else if (source === "backend") {
            setError(`🖥️ Backend Error: ${data.content}`);
          } else {
            setError(`❌ Error: ${data.content}`);
          }
          break;
        }
        case "metrics":
          updateCurrentTurn((t) => {
            t.metrics = {
              askedBy: data.askedBy || null,
              numToolRounds: data.numToolRounds ?? 0,
              durationSeconds: data.durationSeconds ?? 0,
              inputTokens: data.inputTokens ?? 0,
              outputTokens: data.outputTokens ?? 0,
            };
          });
          break;
        case "tool_error":
          updateCurrentTurn((t) => {
            t.thinkingText += `\n[Tool error: ${data.content} — retrying...]\n`;
          });
          break;
        case "done":
          updateCurrentTurn((t) => {
            t.thinkingExpanded = false;
          });
          break;
        default:
          break;
      }
    },
    [updateCurrentTurn],
  );

  const submitQuery = async (event) => {
    if (event && typeof event.preventDefault === "function") {
      event.preventDefault();
    }

    if (!query.trim() || isLoading) {
      return;
    }

    const currentQuery = query;
    setIsLoading(true);
    setTurns((prev) => [...prev, createTurn(currentQuery)]);
    setError(null);
    setQuery("");

    const controller = new AbortController();
    abortControllerRef.current = controller;
    wasCancelledRef.current = false;
    partialAnswerRef.current = "";
    const queryStartTime = Date.now();
    const timeoutId = setTimeout(() => controller.abort(), 5 * 60 * 1000);

    try {
      const response = await AiAssistantApi.postAiQuery(
        currentQuery,
        selectedModel,
        controller.signal,
        { messages: conversationMessages, threadId: activeThreadId, dataScope },
      );

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        // Keep the last (potentially incomplete) line in the buffer
        buffer = lines.pop() || "";

        lines.forEach((line) => {
          if (!line.startsWith("data: ")) return;
          try {
            const data = JSON.parse(line.slice(6));
            handleStreamEvent(data);
          } catch (err) {
            // Ignore parse errors for incomplete chunks
          }
        });
      }

      // Process any remaining buffered data
      if (buffer.startsWith("data: ")) {
        try {
          const data = JSON.parse(buffer.slice(6));
          handleStreamEvent(data);
        } catch (err) {
          // Ignore parse errors
        }
      }
    } catch (err) {
      if (err.name === "AbortError") {
        if (wasCancelledRef.current) {
          const partial = partialAnswerRef.current;
          const askedBy = AuthService.getCurrentUser()?.name ?? null;
          const durationSeconds = (Date.now() - queryStartTime) / 1000;
          const numToolRounds = allToolCallsRef.current.filter(
            (c) => c.type === "tool_use",
          ).length;
          updateCurrentTurn((t) => {
            const prefix = t.answer ? "\n\n" : "";
            t.answer += `${prefix}_Cancelled by user._`;
            t.thinkingExpanded = false;
            // Fill in the fields the server-side `metrics` event would normally
            // set. Without this the Metrics tab would only show the token
            // counts captured by incremental `usage` events.
            t.metrics = {
              askedBy,
              numToolRounds,
              durationSeconds,
              inputTokens: t.metrics?.inputTokens ?? 0,
              outputTokens: t.metrics?.outputTokens ?? 0,
              cancelled: true,
            };
          });
          // Seed conversation history with the cancelled turn so follow-up
          // questions (e.g. "continue but use this data point") have context.
          setConversationMessages((prev) => [
            ...(prev || []),
            { role: "user", content: currentQuery },
            {
              role: "assistant",
              content: [
                {
                  type: "text",
                  text:
                    (partial || "").trim() ||
                    "[Cancelled by user before a response was produced.]",
                },
              ],
            },
          ]);
        } else {
          console.error("Query error:", err);
          setError("⏱️ Request timed out after 5 minutes (Frontend timeout)");
        }
      } else {
        console.error("Query error:", err);
        if (
          err.message.includes("fetch") ||
          err.message.includes("network") ||
          err.message.includes("Failed to fetch")
        ) {
          setError(
            `🔌 Network error - connection lost (Frontend): ${err.message}`,
          );
        } else if (err.message.includes("HTTP error")) {
          setError(`🖥️ Server error (Backend): ${err.message}`);
        } else {
          setError(`❌ Error: ${err.message}`);
        }
      }
    } finally {
      clearTimeout(timeoutId);
      abortControllerRef.current = null;
      setIsLoading(false);
    }
  };

  const handleCancel = useCallback(() => {
    if (abortControllerRef.current) {
      wasCancelledRef.current = true;
      abortControllerRef.current.abort();
    }
  }, []);

  const handleKeyDown = (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      event.preventDefault();
      submitQuery();
    }
  };

  const updateTurnAt = useCallback((index, updater) => {
    setTurns((prev) => {
      if (index < 0 || index >= prev.length) {
        return prev;
      }
      const next = [...prev];
      const updated = { ...next[index] };
      updater(updated);
      next[index] = updated;
      return next;
    });
  }, []);

  const submitFeedback = useCallback(
    async (index, conversationId, rating, comments) => {
      updateTurnAt(index, (t) => {
        t.feedback = {
          ...(t.feedback || {}),
          rating,
          comments: comments || "",
          submitting: true,
          error: null,
        };
      });
      try {
        await AiAssistantApi.submitFeedback(conversationId, rating, comments);
        updateTurnAt(index, (t) => {
          t.feedback = {
            rating,
            comments: comments || "",
            submitted: true,
          };
        });
      } catch (err) {
        updateTurnAt(index, (t) => {
          t.feedback = {
            ...(t.feedback || {}),
            rating,
            comments: comments || "",
            submitting: false,
            submitted: false,
            error: err?.message || "Failed to submit feedback",
          };
        });
      }
    },
    [updateTurnAt],
  );

  const turnObjects = turns.map((turn, index) => ({
    ...turn,
    onToggleThinking: () =>
      updateTurnAt(index, (t) => {
        t.thinkingExpanded = !t.thinkingExpanded;
      }),
    onSetActiveTab: (tab) =>
      updateTurnAt(index, (t) => {
        t.activeTab = tab;
      }),
    onSetFeedbackDraft: (draft) =>
      updateTurnAt(index, (t) => {
        t.feedback = draft;
      }),
    onSubmitFeedback: (rating, comments) =>
      submitFeedback(index, turn.conversationId, rating, comments),
  }));

  return (
    <Modal onClose={onClose} className="ai-assistant-modal">
      <div className="ai-assistant-modal__header">
        <div>
          <p className="fw-900 xl-text">AI Assistant</p>
          <p className="text-small accent-text">
            Ask questions about engagement data and review the output.
          </p>
        </div>
        <div className="ai-assistant-modal__header-actions">
          <button
            type="button"
            className="btn custom-btn btn-outline"
            onClick={handleNew}
            disabled={
              isLoading || (selectedHistoryId == null && turns.length === 0)
            }
          >
            New
          </button>
          <AiAssistantHistory
            onSelect={handleHistorySelect}
            selectedThreadId={selectedHistoryId}
            disabled={isLoading}
          />
          <button
            type="button"
            className="btn custom-btn btn-outline"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>

      {modelWarning && (
        <div
          className={`ai-assistant-modal__model-warning alert ${
            modelWarning.level === "error"
              ? "alert-danger"
              : modelWarning.level === "info"
                ? "alert-info"
                : "alert-warning"
          }`}
        >
          {modelWarning.message}
        </div>
      )}

      <div className="ai-assistant-modal__body" ref={threadRef}>
        {!hasTurns && !isLoading && (
          <div className="ai-assistant-modal__empty-state">
            <div className="ai-assistant-modal__empty-state-icon">
              <ChatIcon style={{ fontSize: 28 }} />
            </div>
            <p className="ai-assistant-modal__empty-state-text">
              Ask a question about your engagement data. I'll write
              <br />
              and run SQL queries to find the answer.
            </p>
          </div>
        )}

        {error && (
          <div className="ai-assistant-modal__error alert alert-danger">
            {error}
          </div>
        )}

        {turnObjects.map((turn, index) => (
          <TurnCard
            key={index}
            turn={turn}
            turnIndex={index}
            isStreaming={isLoading && index === turnObjects.length - 1}
            isHistorical={selectedHistoryId != null}
            thinkingBoxRef={thinkingBoxRef}
          />
        ))}
      </div>

      <div className="ai-assistant-modal__input-bar">
        <div className="ai-assistant-modal__input-options">
          <div className="ai-assistant-modal__radio-group">
            <label className="ai-assistant-modal__radio-label">
              <input
                type="radio"
                name="dataScope"
                value={DATA_SCOPE.ENGAGEMENT}
                checked={dataScope === DATA_SCOPE.ENGAGEMENT}
                onChange={() => setDataScope(DATA_SCOPE.ENGAGEMENT)}
                disabled={hasTurns || !!selectedHistoryId}
              />
              Engagement Program only
            </label>
            <label className="ai-assistant-modal__radio-label">
              <input
                type="radio"
                name="dataScope"
                value={DATA_SCOPE.ALL}
                checked={dataScope === DATA_SCOPE.ALL}
                onChange={() => setDataScope(DATA_SCOPE.ALL)}
                disabled={hasTurns || !!selectedHistoryId}
              />
              All Chabad Houses
            </label>
          </div>
          <div className="ai-assistant-modal__input-options-spacer" />
          <select
            className="ai-assistant-modal__model-select"
            value={selectedModel}
            onChange={(event) => setSelectedModel(event.target.value)}
            disabled={isLoading || !!selectedHistoryId}
          >
            {models.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
        </div>
        <div className="ai-assistant-modal__input-row">
          <textarea
            ref={textareaRef}
            className="ai-assistant-modal__textarea"
            placeholder={
              hasTurns && !selectedHistoryId
                ? "Ask a follow-up question..."
                : "e.g., How many active students do we have?"
            }
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={handleKeyDown}
            disabled={isLoading || selectedHistoryId != null}
            rows={2}
          />
          {isLoading ? (
            <button
              className="btn custom-btn btn-outline ai-assistant-modal__run-btn"
              type="button"
              onClick={handleCancel}
            >
              Stop
            </button>
          ) : (
            <button
              className="btn custom-btn btn-accent ai-assistant-modal__run-btn"
              type="button"
              onClick={submitQuery}
              disabled={!query.trim() || selectedHistoryId != null}
              title={shortcutLabel}
            >
              {hasTurns && !selectedHistoryId ? "Send" : "Run"}
            </button>
          )}
        </div>
      </div>
    </Modal>
  );
}
